import type { Car } from '@multirx/shared';
import { carPower, effectiveStart, effectiveStraightLine } from '@multirx/shared/types/car.js';
import { clampChampionshipStarLevel } from '@multirx/shared/game/starLadder.js';
import { mountInteractiveStarsCar } from '../components/starRating.js';
import { mountEntityImageBlock } from '../ui/entityImageBlock.js';
import {
  mountGarageEntityRename,
  normalizeGarageCarName,
  MAX_GARAGE_CAR_NAME_LENGTH,
} from '../garage/garageEntityRename.js';
import {
  assignCarImageFromLibrary,
  assignCarModel3dPath,
  updateCar,
  uploadCarImage,
  uploadCarModel3d,
} from './api.js';
import { mountCar3dPreview, type Car3dPreviewHandle } from './car3dPreview.js';
import { resolveCar3dModelUrl } from './car3dModelMap.js';
import { cacheBustCarGlbUrl } from './carOrientationFix.js';
import { openCar3dModelPicker } from '../ui/car3dModelPicker.js';
import { applyRoleVisibility } from '../auth/navAccount.js';
import {
  pairedCarImagePath,
  pairedCarModel3dPath,
  readCarMediaLinkPreference,
  writeCarMediaLinkPreference,
} from './carMediaLink.js';

export type CarDetailHandlers = {
  onSaved?: (updated: Car) => void;
  onDelete?: (car: Car) => void;
};

let active3dPreview: Car3dPreviewHandle | null = null;

function normalizeHandlers(
  handlers?: CarDetailHandlers | ((updated: Car) => void)
): CarDetailHandlers {
  if (typeof handlers === 'function') return { onSaved: handlers };
  return handlers ?? {};
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

function clampCarStat(v: number): number {
  return clampChampionshipStarLevel(v);
}

export function renderCarDetail(
  container: HTMLElement,
  car: Car | null,
  handlers?: CarDetailHandlers | ((updated: Car) => void)
): void {
  const { onSaved, onDelete } = normalizeHandlers(handlers);
  active3dPreview?.dispose();
  active3dPreview = null;
  container.innerHTML = '';

  if (car === null) {
    container.innerHTML = `
      <div class="circuits-detail-empty">
        <p>Sélectionnez une voiture dans la liste pour afficher son détail.</p>
      </div>`;
    return;
  }

  const detailCar: Car = car;
  const carId = detailCar.id;
  const lineEff = effectiveStraightLine(detailCar);
  const startEff = effectiveStart(detailCar);
  const total = carPower(detailCar);
  const mediaLinked = readCarMediaLinkPreference(true);

  const section = document.createElement('div');
  section.className = 'circuits-detail car-detail';
  section.innerHTML = `
    <header class="circuits-detail-header car-detail-header">
      <div class="car-detail-header-left circuits-detail-header-main">
        <div class="circuits-detail-title-row">
          <h2 class="circuits-detail-title">#${detailCar.id} · ${escapeHtml(detailCar.name)}</h2>
          <button type="button" class="circuits-detail-rename-trigger" aria-label="Renommer la voiture" title="Renommer">✎</button>
        </div>
        <span class="circuits-detail-muted">Total ${total}★${detailCar.division != null ? ` · D${detailCar.division}` : ''}</span>
        <p class="circuits-detail-rename-status" role="status" hidden></p>
      </div>
    </header>
    <div class="car-detail-media">
      <label class="car-detail-media-link">
        <input type="checkbox" class="car-detail-media-link-input" ${mediaLinked ? 'checked' : ''} />
        <span>Lier image et modèle 3D</span>
        <span class="car-detail-media-link-hint">même nom de fichier → changer l’un change l’autre</span>
      </label>
      <div class="car-detail-media-grid">
        <div class="car-detail-media-col car-detail-media-col--3d">
          <div class="car-detail-3d" data-car-3d aria-hidden="true"></div>
          <div class="detail-model3d-row" data-car-model3d>
            <div class="detail-model3d-info">
              <span class="detail-model3d-label">Modèle 3D</span>
              <span class="detail-model3d-current"></span>
            </div>
            <button type="button" class="btn btn-primary btn-sm detail-model3d-btn">Changer</button>
          </div>
        </div>
        <div class="car-detail-media-col car-detail-media-col--image" data-car-image-host></div>
      </div>
    </div>
    <p class="detail-effective-bonuses">Bonus course : ligne droite <strong>+${lineEff}</strong> · départ <strong>+${startEff}</strong></p>
    <div class="detail-edit-form" data-car-id="${detailCar.id}">
      <div class="detail-form-loader" aria-hidden="true">
        <span class="spinner"></span>
        <span>Sauvegarde…</span>
      </div>
      <div class="detail-edit-row" data-stat="engine" data-current-value="${detailCar.engine}">
        <label>Moteur (ligne droite)</label>
        <span class="star-display"></span>
      </div>
      <div class="detail-edit-row" data-stat="tire" data-current-value="${detailCar.tire}">
        <label>Pneu (départ)</label>
        <span class="star-display"></span>
      </div>
    </div>`;

  const linkInput = section.querySelector<HTMLInputElement>('.car-detail-media-link-input');
  linkInput?.addEventListener('change', () => {
    writeCarMediaLinkPreference(Boolean(linkInput.checked));
  });

  const isMediaLinked = (): boolean => Boolean(linkInput?.checked);

  const preview3dContainer = section.querySelector<HTMLElement>('[data-car-3d]');
  if (preview3dContainer) {
    active3dPreview = mountCar3dPreview(
      preview3dContainer,
      cacheBustCarGlbUrl(resolveCar3dModelUrl(detailCar))
    );
  }

  const reloadDetail = (saved: Car): void => {
    onSaved?.(saved);
    renderCarDetail(container, saved, handlers);
  };

  async function applyModel3d(path: string | null): Promise<Car> {
    if (carId == null) throw new Error('Voiture sans id');
    let saved = await assignCarModel3dPath(carId, path);
    if (isMediaLinked() && path) {
      const imagePair = pairedCarImagePath(path);
      if (imagePair) {
        try {
          saved = await assignCarImageFromLibrary(carId, imagePair);
        } catch {
          /* paire image absente : on garde le modèle seul */
        }
      }
    }
    return saved;
  }

  async function applyImageThenMaybeModel(applyImage: () => Promise<Car>): Promise<Car> {
    if (carId == null) throw new Error('Voiture sans id');
    let saved = await applyImage();
    if (isMediaLinked() && saved.imagePath) {
      const modelPair = pairedCarModel3dPath(saved.imagePath);
      if (modelPair) {
        try {
          saved = await assignCarModel3dPath(carId, modelPair);
        } catch {
          /* paire 3D absente : on garde l’image seule */
        }
      }
    }
    return saved;
  }

  const model3dCurrent = section.querySelector('.detail-model3d-current');
  if (model3dCurrent) {
    model3dCurrent.textContent = detailCar.model3dPath
      ? (detailCar.model3dPath.split('/').pop() ?? '').replace(/\.glb$/i, '')
      : 'Non assigné';
  }
  const model3dBtn = section.querySelector('.detail-model3d-btn');
  model3dBtn?.addEventListener('click', () => {
    if (carId == null) return;
    openCar3dModelPicker({
      title: 'Choisir un modèle 3D',
      currentPath: detailCar.model3dPath ?? null,
      onSelect: (item) => {
        void applyModel3d(item ? item.publicPath : null).then(reloadDetail);
      },
      onUploadFromPc: (file) =>
        uploadCarModel3d(carId, file).then(async (saved) => {
          if (isMediaLinked() && saved.model3dPath) {
            const imagePair = pairedCarImagePath(saved.model3dPath);
            if (imagePair) {
              try {
                saved = await assignCarImageFromLibrary(carId, imagePair);
              } catch {
                /* ignore */
              }
            }
          }
          reloadDetail(saved);
        }),
    });
  });

  const imageHost = section.querySelector<HTMLElement>('[data-car-image-host]');
  if (carId != null && imageHost) {
    mountEntityImageBlock(section, {
      entityId: carId,
      collection: 'cars',
      imagePath: detailCar.imagePath,
      alt: `Voiture ${detailCar.name}`,
      pickerTitle: 'Choisir un visuel de voiture',
      mountIn: imageHost,
      handlers: {
        uploadFromPc: (file) => applyImageThenMaybeModel(() => uploadCarImage(carId, file)),
        assignFromLibrary: (publicPath) =>
          applyImageThenMaybeModel(() => assignCarImageFromLibrary(carId, publicPath)),
      },
      onAfterSave: (saved) => reloadDetail(saved as Car),
    });
  }

  const form = section.querySelector('.detail-edit-form') as HTMLElement;
  const rows = section.querySelectorAll('.detail-edit-row');

  function getCurrentCar(): Car {
    const data: Record<string, number> = {
      engine: detailCar.engine,
      tire: detailCar.tire,
    };
    rows.forEach((row) => {
      const stat = (row as HTMLElement).dataset.stat!;
      const val = (row as HTMLElement).dataset.currentValue;
      if (val != null) data[stat] = clampCarStat(Number(val));
    });
    return {
      id: detailCar.id,
      name: detailCar.name,
      engine: data.engine,
      tire: data.tire,
      imagePath: detailCar.imagePath,
      model3dPath: detailCar.model3dPath,
    };
  }

  function saveCar(): void {
    const updated = getCurrentCar();
    if (updated.id == null) return;
    form.classList.add('detail-edit-form--saving');
    updateCar(updated.id, {
      name: updated.name,
      engine: updated.engine,
      tire: updated.tire,
    })
      .then((saved) => reloadDetail(saved))
      .catch(() => {
        form.classList.remove('detail-edit-form--saving');
      });
  }

  rows.forEach((row) => {
    const stat = (row as HTMLElement).dataset.stat!;
    const display = row.querySelector('.star-display') as HTMLElement;
    const initial = clampCarStat(
      Number((row as HTMLElement).dataset.currentValue ?? detailCar[stat as keyof Car])
    );
    if (display) {
      mountInteractiveStarsCar(display, initial, (value) => {
        (row as HTMLElement).dataset.currentValue = String(value);
        saveCar();
      });
    }
  });

  const titleRow = section.querySelector('.circuits-detail-title-row') as HTMLElement;
  const renameBtn = section.querySelector('.circuits-detail-rename-trigger');
  if (onDelete && titleRow && renameBtn) {
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'circuits-detail-delete-btn';
    deleteBtn.setAttribute('aria-label', 'Supprimer');
    deleteBtn.title = 'Supprimer';
    deleteBtn.dataset.role = 'admin';
    deleteBtn.textContent = '🗑';
    deleteBtn.addEventListener('click', () => onDelete(detailCar));
    renameBtn.after(deleteBtn);
  }
  mountGarageEntityRename({
    section,
    titleRow,
    fields: [
      {
        id: 'name',
        label: 'Nom de la voiture',
        value: detailCar.name,
        maxLength: MAX_GARAGE_CAR_NAME_LENGTH,
        placeholder: 'Ex. Alpine A110',
      },
    ],
    onSave: async (values) => {
      const name = normalizeGarageCarName(values.name ?? '');
      if (!name) {
        throw new Error(`Entrez un nom (1–${MAX_GARAGE_CAR_NAME_LENGTH} caractères).`);
      }
      const current = getCurrentCar();
      if (current.id == null) return;
      const saved = await updateCar(current.id, {
        name,
        engine: current.engine,
        tire: current.tire,
      });
      reloadDetail(saved);
    },
    onRestore: () => renderCarDetail(container, detailCar, handlers),
  });

  container.appendChild(section);
  applyRoleVisibility();
}

export function setCarDetailLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    container.innerHTML =
      '<div class="circuits-loading"><span class="spinner"></span><p>Chargement de la voiture…</p></div>';
  }
}

export function setCarDetailError(container: HTMLElement, message: string): void {
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}
